import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { uploadMedia } from "@/lib/upload";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Save, Loader2, RotateCcw, Upload, Type, Image as ImageIcon, Music, Trash2, FileText, HelpCircle, Plus } from "lucide-react";
import { ASSETS, HERO, ESCAPECORE, PSYCHOACTIVE, STORY, NIGHT_VISION, GAME_INFO, GALLERY, REVIEWS, CONTACT_SECTION, CONTACT, SLOGANS, FAQ } from "@/data/content";

const TEXT_FIELDS = [
  { g: "Úvod (hero)", items: [
    { k: "hero.kicker", l: "Nadtitulek", d: HERO.kicker },
    { k: "hero.slogan", l: "Hlavní slogan", d: HERO.slogan, m: true },
    { k: "hero.sub", l: "Podtitulek", d: HERO.sub, m: true },
    { k: "hero.cta", l: "Tlačítko", d: HERO.cta },
  ]},
  { g: "O hře — Escapecore", items: [
    { k: "escapecore.title", l: "Nadpis", d: ESCAPECORE.title },
    { k: "escapecore.body", l: "Text", d: ESCAPECORE.body, m: true },
  ]},
  { g: "O hře — Psychoactive", items: [
    { k: "psychoactive.title", l: "Nadpis", d: PSYCHOACTIVE.title },
    { k: "psychoactive.body", l: "Text", d: PSYCHOACTIVE.body, m: true },
  ]},
  { g: "O hře — Příběh", items: [
    { k: "story.title", l: "Nadpis", d: STORY.title },
    { k: "story.body", l: "Text", d: STORY.body, m: true },
    { k: "story.quote", l: "Citát u obrázku", d: STORY.quote, m: true },
  ]},
  { g: "Noční vidění", items: [
    { k: "nightvision.title", l: "Nadpis", d: NIGHT_VISION.title },
    { k: "nightvision.body", l: "Text", d: NIGHT_VISION.body, m: true },
  ]},
  { g: "Info o hře", items: [
    { k: "gameinfo.priceNote", l: "Poznámka k ceně", d: GAME_INFO.priceNote, m: true },
    { k: "gameinfo.notice", l: "Upozornění", d: GAME_INFO.notice },
  ]},
  { g: "Foto křesla", items: [
    { k: "chair.slogan", l: "Slogan", d: SLOGANS[1], m: true },
  ]},
  { g: "Galerie", items: [
    { k: "gallery.kicker", l: "Nadtitulek", d: GALLERY.kicker },
    { k: "gallery.title", l: "Nadpis", d: GALLERY.title },
    { k: "gallery.sub", l: "Podtitulek", d: GALLERY.sub },
  ]},
  { g: "Recenze", items: [
    { k: "reviews.kicker", l: "Nadtitulek", d: REVIEWS.kicker },
    { k: "reviews.title", l: "Nadpis", d: REVIEWS.title },
    { k: "reviews.sub", l: "Podtitulek", d: REVIEWS.sub },
  ]},
  { g: "Kontakt", items: [
    { k: "contact.intro", l: "Úvodní text", d: CONTACT_SECTION.intro, m: true },
  ]},
  { g: "Kontaktní údaje", items: [
    { k: "contact.phone", l: "Telefon", d: CONTACT.phone },
    { k: "contact.email", l: "E-mail", d: CONTACT.email },
    { k: "contact.address", l: "Adresa", d: CONTACT.address, m: true },
  ]},
  { g: "Rezervace", items: [
    { k: "booking.title", l: "Nadpis sekce", d: "Vyber si termín" },
    { k: "booking.subtitle", l: "Podtitulek (prázdné = skrýt)", d: "Vyberte volný termín a dokončete rezervaci.", m: true },
    { k: "booking.info", l: "Doplňující info k rezervaci (prázdné = skrýt)", d: "", m: true },
    { k: "booking.free_note", l: "Poznámka když je rezervace zdarma", d: "Rezervace je zdarma, neplatí se žádná záloha.", m: true },
    { k: "booking.success_title", l: "Nadpis po úspěšné rezervaci", d: "Rezervace potvrzena" },
    { k: "booking.success_body", l: "Text po úspěšné rezervaci", d: "Potvrzení jsme vám poslali na email. Těšíme se na vás!", m: true },
    { k: "booking.empty", l: "Text když nejsou vypsané termíny", d: "Momentálně nejsou vypsané žádné termíny. Zkuste to prosím později.", m: true },
    { k: "booking.error", l: "Text při nezdařené rezervaci", d: "Rezervaci se nepodařilo dokončit.", m: true },
  ]},
];

const IMAGE_FIELDS = [
  { g: "Loga", items: [
    { k: "logoGate", l: "Logo GATE ESCAPE (navigace + patička)", d: ASSETS.logoGate },
    { k: "logoPsychoactive", l: "Logo Psychoactive (hero)", d: ASSETS.logoPsychoactive },
    { k: "bootIcon", l: "Ikona chatu (holínka)", d: ASSETS.bootIcon },
  ]},
  { g: "Hlavní obrázky", items: [
    { k: "hero1", l: "Hero — pozadí úvodu", d: ASSETS.hero1 },
    { k: "hero2", l: "O hře — interiér", d: ASSETS.hero2 },
    { k: "chair", l: "Foto křesla / ošetřovna", d: ASSETS.chair },
    { k: "tablet", l: "Noční vidění — tablet", d: ASSETS.tablet },
  ]},
  { g: "Galerie (polaroidy)", items: ASSETS.polaroids.map((d, i) => ({ k: `polaroid${i + 1}`, l: `Polaroid č. ${i + 1}`, d })) },
];

function ImageCard({ f, current, onUpload, onReset }) {
  const [busy, setBusy] = useState(false);
  const src = current || f.d;
  const handle = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Vyber prosím obrázek."); return; }
    setBusy(true);
    try { const url = await uploadMedia(file); onUpload(url); toast.success("Obrázek nahrán. Nezapomeň uložit."); }
    catch (err) { toast.error(err.response?.data?.detail || "Nahrání selhalo."); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-white/10 bg-black p-3" data-testid={`image-card-${f.k}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-neutral-400 truncate">{f.l}</span>
        {current && <button onClick={onReset} data-testid={`image-reset-${f.k}`} className="text-[10px] text-red-400 hover:text-red-300 flex items-center gap-1"><Trash2 className="w-3 h-3" /> Odebrat</button>}
      </div>
      <div className="h-28 border border-white/10 bg-[#0b0a09] overflow-hidden flex items-center justify-center mb-2">
        {src ? <img src={src} alt={f.l} className="w-full h-full object-contain" /> : <span className="text-[10px] text-neutral-600">bez obrázku</span>}
      </div>
      <label className="hud-btn vhs-btn flex items-center justify-center gap-2 px-3 py-1.5 text-xs font-head cursor-pointer">
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Nahrát
        <input type="file" accept="image/*" className="hidden" onChange={handle} data-testid={`image-input-${f.k}`} disabled={busy} />
      </label>
    </div>
  );
}

export default function AdminTexts() {
  const [tab, setTab] = useState("texty");
  const [texts, setTexts] = useState({});
  const [images, setImages] = useState({});
  const [music, setMusic] = useState("");
  const [rows, setRows] = useState([]);
  const [faq, setFaq] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [musicBusy, setMusicBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => {
      setTexts(data.site_texts || {});
      setImages(data.site_images || {});
      setMusic(data.bg_music_url || "");
      setRows(data.game_info_rows && data.game_info_rows.length ? data.game_info_rows : GAME_INFO.rows.map((r) => ({ ...r })));
      setFaq(data.faq_items && data.faq_items.length ? data.faq_items : FAQ.items.map((i) => ({ ...i })));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const setT = (k, v) => setTexts((p) => ({ ...p, [k]: v }));
  const resetT = (k) => setTexts((p) => { const n = { ...p }; delete n[k]; return n; });
  const setImg = (k, v) => setImages((p) => ({ ...p, [k]: v }));
  const resetImg = (k) => setImages((p) => { const n = { ...p }; delete n[k]; return n; });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        site_texts: texts, site_images: images, bg_music_url: music,
        game_info_rows: rows.filter((r) => (r.label || "").trim() || (r.value || "").trim()),
        faq_items: faq.filter((f) => (f.q || "").trim() || (f.a || "").trim()),
      });
      toast.success("Obsah uložen.");
    } catch (e) { toast.error("Uložení selhalo."); }
    finally { setSaving(false); }
  };

  const uploadMusic = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("audio/")) { toast.error("Vyber prosím zvukový soubor (MP3)."); return; }
    setMusicBusy(true);
    try { const url = await uploadMedia(file); setMusic(url); toast.success("Hudba nahrána. Nezapomeň uložit."); }
    catch (err) { toast.error(err.response?.data?.detail || "Nahrání selhalo."); }
    finally { setMusicBusy(false); }
  };

  if (loading) return <div className="p-4 text-neutral-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Načítám…</div>;

  const TabBtn = ({ id, icon: Icon, label }) => (
    <button onClick={() => setTab(id)} data-testid={`obsah-tab-${id}`}
      className={`flex items-center gap-2 px-4 py-2 text-sm font-head border ${tab === id ? "bg-red-700 text-white border-red-700" : "border-white/10 text-neutral-400 hover:text-neutral-100"}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );

  return (
    <div className="space-y-5" data-testid="admin-texts">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2">
          <TabBtn id="texty" icon={Type} label="Texty" />
          <TabBtn id="obrazky" icon={ImageIcon} label="Obrázky" />
          <TabBtn id="hudba" icon={Music} label="Hudba" />
          <TabBtn id="spis" icon={FileText} label="Spis HUBERT" />
          <TabBtn id="faq" icon={HelpCircle} label="FAQ" />
        </div>
        <button onClick={save} disabled={saving} data-testid="save-content-btn" className="hud-btn vhs-btn flex items-center gap-2 px-4 py-2 text-sm font-head">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Uložit vše
        </button>
      </div>

      {tab === "texty" && (
        <div className="space-y-5">
          <p className="text-xs text-neutral-500">Uprav libovolný text. Prázdné pole = použije se původní text.</p>
          {TEXT_FIELDS.map((grp) => (
            <div key={grp.g} className="border border-white/10 bg-[#0b0a09] p-4">
              <p className="font-head text-sm text-nv mb-3 tracking-widest uppercase">{grp.g}</p>
              <div className="space-y-3">
                {grp.items.map((f) => (
                  <div key={f.k}>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs text-neutral-400">{f.l}</label>
                      {texts[f.k] !== undefined && <button onClick={() => resetT(f.k)} className="text-[10px] text-neutral-600 hover:text-nv flex items-center gap-1"><RotateCcw className="w-3 h-3" /> původní</button>}
                    </div>
                    {f.m ? (
                      <Textarea data-testid={`text-${f.k}`} value={texts[f.k] ?? f.d} onChange={(e) => setT(f.k, e.target.value)} rows={3} className="rounded-none bg-black border-white/15 focus-visible:ring-nv text-sm" />
                    ) : (
                      <Input data-testid={`text-${f.k}`} value={texts[f.k] ?? f.d} onChange={(e) => setT(f.k, e.target.value)} className="rounded-none bg-black border-white/15 focus-visible:ring-nv text-sm" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "obrazky" && (
        <div className="space-y-5">
          <p className="text-xs text-neutral-500">Nahraj vlastní obrázek pro výměnu. Bez nahrání zůstane původní obrázek.</p>
          {IMAGE_FIELDS.map((grp) => (
            <div key={grp.g} className="border border-white/10 bg-[#0b0a09] p-4">
              <p className="font-head text-sm text-nv mb-3 tracking-widest uppercase">{grp.g}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {grp.items.map((f) => (
                  <ImageCard key={f.k} f={f} current={images[f.k]} onUpload={(url) => setImg(f.k, url)} onReset={() => resetImg(f.k)} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "hudba" && (
        <div className="border border-white/10 bg-[#0b0a09] p-5 space-y-4 max-w-xl" data-testid="music-panel">
          <div>
            <p className="font-head text-sm text-nv tracking-widest uppercase mb-1">Podkresová hudba</p>
            <p className="text-xs text-neutral-500">Nahraj vlastní MP3. Návštěvník ji spouští tlačítkem zvuku v navigaci.</p>
          </div>
          {music ? (
            <div className="space-y-3">
              <audio controls src={music} className="w-full" data-testid="music-preview" />
              <div className="flex items-center gap-3">
                <label className="hud-btn vhs-btn flex items-center gap-2 px-3 py-1.5 text-xs font-head cursor-pointer">
                  {musicBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Vyměnit
                  <input type="file" accept="audio/*" className="hidden" onChange={uploadMusic} data-testid="music-input" disabled={musicBusy} />
                </label>
                <button onClick={() => setMusic("")} data-testid="music-remove-btn" className="flex items-center gap-2 px-3 py-1.5 text-xs text-red-400 border border-red-900/50 hover:bg-red-950/30">
                  <Trash2 className="w-3 h-3" /> Odebrat
                </button>
              </div>
            </div>
          ) : (
            <label className="hud-btn vhs-btn flex items-center justify-center gap-2 px-4 py-3 text-sm font-head cursor-pointer w-fit">
              {musicBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Music className="w-4 h-4" />} Nahrát MP3
              <input type="file" accept="audio/*" className="hidden" onChange={uploadMusic} data-testid="music-input" disabled={musicBusy} />
            </label>
          )}
        </div>
      )}

      {tab === "spis" && (
        <div className="space-y-3" data-testid="spis-editor">
          <p className="text-xs text-neutral-500">Uprav řádky ve „Spisu HUBERT" (Složka / Dossier). Můžeš přidat nebo odebrat políčka.</p>
          {rows.map((r, i) => (
            <div key={i} className="flex gap-2 items-center border border-white/10 bg-[#0b0a09] p-3" data-testid={`spis-row-${i}`}>
              <Input value={r.label} onChange={(e) => setRows((p) => p.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} placeholder="Popisek (např. Délka hry)" data-testid={`spis-label-${i}`} className="rounded-none bg-black border-white/15 text-sm" />
              <Input value={r.value} onChange={(e) => setRows((p) => p.map((x, j) => j === i ? { ...x, value: e.target.value } : x))} placeholder="Hodnota (např. 90 minut)" data-testid={`spis-value-${i}`} className="rounded-none bg-black border-white/15 text-sm" />
              <button onClick={() => setRows((p) => p.filter((_, j) => j !== i))} data-testid={`spis-remove-${i}`} className="p-2 text-red-400 hover:bg-red-950/30 border border-red-900/40 shrink-0"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <button onClick={() => setRows((p) => [...p, { label: "", value: "" }])} data-testid="spis-add" className="hud-btn vhs-btn flex items-center gap-2 px-3 py-2 text-xs font-head"><Plus className="w-4 h-4" /> Přidat políčko</button>
        </div>
      )}

      {tab === "faq" && (
        <div className="space-y-3" data-testid="faq-editor">
          <p className="text-xs text-neutral-500">Uprav časté dotazy. Můžeš přidat nebo odebrat otázky.</p>
          {faq.map((f, i) => (
            <div key={i} className="border border-white/10 bg-[#0b0a09] p-3 space-y-2" data-testid={`faq-row-${i}`}>
              <div className="flex gap-2 items-center">
                <Input value={f.q} onChange={(e) => setFaq((p) => p.map((x, j) => j === i ? { ...x, q: e.target.value } : x))} placeholder="Otázka" data-testid={`faq-q-${i}`} className="rounded-none bg-black border-white/15 text-sm font-head" />
                <button onClick={() => setFaq((p) => p.filter((_, j) => j !== i))} data-testid={`faq-remove-${i}`} className="p-2 text-red-400 hover:bg-red-950/30 border border-red-900/40 shrink-0"><Trash2 className="w-4 h-4" /></button>
              </div>
              <Textarea value={f.a} onChange={(e) => setFaq((p) => p.map((x, j) => j === i ? { ...x, a: e.target.value } : x))} rows={3} placeholder="Odpověď" data-testid={`faq-a-${i}`} className="rounded-none bg-black border-white/15 text-sm" />
            </div>
          ))}
          <button onClick={() => setFaq((p) => [...p, { q: "", a: "" }])} data-testid="faq-add" className="hud-btn vhs-btn flex items-center gap-2 px-3 py-2 text-xs font-head"><Plus className="w-4 h-4" /> Přidat dotaz</button>
        </div>
      )}
    </div>
  );
}
